/**
 * Always-visible Tally connection status pill.
 *
 * Pings `/api/v1/aiaccountant/tally-ping` every 30s. Colours:
 *   • green — Tally responded
 *   • amber — no URL configured
 *   • red   — URL configured but ping failed
 *
 * Click to open Settings (parent passes onOpenSettings).
 */

import { useEffect, useRef, useState } from 'react';
import { useSettings } from '@/lib/hooks';
import { pingTally } from '@/lib/aiaccountant';
import { getAccessToken } from '@/lib/auth';
import clsx from 'clsx';

interface TallyStatusProps {
  onOpenSettings: () => void;
}

type State = 'unconfigured' | 'pinging' | 'online' | 'offline' | 'idle';

export function TallyStatus({ onOpenSettings }: TallyStatusProps) {
  const [settings] = useSettings();
  const [state, setState] = useState<State>('unconfigured');
  const [latency, setLatency] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!settings.tally_url) {
      setState('unconfigured');
      setLatency(null);
      return;
    }
    if (!getAccessToken()) {
      setState('idle');
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failCount = 0;

    const tick = async () => {
      if (cancelled) return;
      setState((cur) => (cur === 'online' ? cur : 'pinging'));
      const start = performance.now();
      const result = await pingTally(settings.tally_url);
      if (cancelled) return;

      if (result.online) {
        failCount = 0;
        setState('online');
        setLatency(Math.round(performance.now() - start));
        timer = setTimeout(tick, 30_000);
      } else {
        failCount += 1;
        setState('offline');
        setLatency(null);
        // Back off: 30s → 2min → 5min → 10min, then keep retrying every 10min.
        // Never stop — Tally restarts happen and we should auto-recover.
        const delays = [30_000, 120_000, 300_000];
        const next = delays[Math.min(failCount - 1, delays.length - 1)];
        timer = setTimeout(tick, next);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [settings.tally_url, retryCount]);

  const dotColour = {
    unconfigured: 'bg-amber-500',
    idle:         'bg-muted-foreground/40',
    pinging:      'bg-blue-500 animate-pulse',
    online:       'bg-green-500',
    offline:      'bg-amber-500',
  }[state];

  const label = {
    unconfigured: 'Tally: configure',
    idle:         'Tally: configure',
    pinging:      'Tally: checking…',
    online:       latency != null ? `Tally: online · ${latency}ms` : 'Tally: online',
    offline:      'Tally: not connected · retry',
  }[state];

  const handleClick = () => {
    if (state === 'offline') {
      // Immediate retry without opening settings
      setRetryCount((n) => n + 1);
    } else {
      onOpenSettings();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded hover:bg-muted"
      title={state === 'offline' ? 'Click to retry connection' : state === 'online' ? 'Tally is reachable' : 'Click to configure Tally URL'}
    >
      <span className={clsx('w-2 h-2 rounded-full', dotColour)}></span>
      {label}
    </button>
  );
}
